import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Building2, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { isEnglish } from '../lib/english';
import { enumLabel, projectStatusLabel, useI18n } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { crStatusLabel } from './ClientCrs';
import { DriveRelated } from './Drive';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * CR-001 §2–§5, §31, §32 — the client is a company. Clients are managed from
 * both systems, on the same form: here with their contacts and projects, which
 * is what the admin attributes work to; in Finance with their invoices,
 * payments, contracts and financial summary as well — tabs only Finance has.
 */

interface Country {
  code: string;
  name: string;
  nameAr: string | null;
  defaultCurrency: string | null;
}

export interface Currency {
  code: string;
  name: string;
  nameAr: string | null;
  symbol: string | null;
  /** Today's rate to the base currency, from Settings > Currencies. */
  rateToBase: number | null;
  isBase: boolean;
}

export function useCountries() {
  return useQuery({
    queryKey: ['countries'],
    queryFn: () => api.get<Country[]>('/countries'),
    staleTime: Infinity,
  });
}

export function useCurrencies() {
  return useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get<Currency[]>('/currencies'),
    staleTime: Infinity,
  });
}

export function ClientsPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const countries = useCountries();
  const currencies = useCurrencies();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['clients', page, filters],
    queryFn: () => api.get<Paginated<any>>('/clients', { page, pageSize: 25, ...filters }),
  });

  return (
    <>
      <PageHeader
        title={t('clients.title')}
        action={
          can(P.CLIENTS_CREATE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/clients/new')}>
              {t('clients.new')}
            </Button>
          )
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-4">
          <Input
            placeholder={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }}
          />
          <Select
            value={filters.countryCode ?? ''}
            onChange={(e) => { setFilters({ ...filters, countryCode: e.target.value }); setPage(1); }}
          >
            <option value="">{t('clients.country')} — {t('common.all')}</option>
            {(countries.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{localized(c)}</option>
            ))}
          </Select>
          <Select
            value={filters.currency ?? ''}
            onChange={(e) => { setFilters({ ...filters, currency: e.target.value }); setPage(1); }}
          >
            <option value="">{t('common.currency')} — {t('common.all')}</option>
            {(currencies.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
          <Select
            value={filters.status ?? ''}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
          >
            <option value="">{t('common.status')} — {t('common.all')}</option>
            <option value="ACTIVE">{enumLabel('ACTIVE', locale)}</option>
            <option value="INACTIVE">{enumLabel('INACTIVE', locale)}</option>
          </Select>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<Building2 className="h-8 w-8" />}
            title={t('clients.noneYet')}
            hint={t('clients.noneYetHint')}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('clients.code')}</Th>
                  <Th>{t('clients.company')}</Th>
                  <Th>{t('clients.country')}</Th>
                  <Th>{t('common.currency')}</Th>
                  <Th align="end">{t('nav.projects')}</Th>
                  <Th align="end">{t('nav.invoices')}</Th>
                  <Th>{t('common.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((client) => (
                  <Tr key={client.id} onClick={() => navigate(`/clients/${client.id}`)}>
                    <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{client.code}</span></Td>
                    <Td>
                      <span className="inline-flex flex-wrap items-center gap-2">
                        {locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName}
                        {/* Any sign-in that works: the company's own, or one of its contacts'. */}
                        {(client.portalSignIns ?? (client.account?.isActive ? 1 : 0)) > 0 && (
                          <Badge tone="plasma">{t('clients.portalBadge')}</Badge>
                        )}
                      </span>
                    </Td>
                    <Td className="text-muted">{localized(client.country)}</Td>
                    <Td className="font-mono text-mono-sm">{client.defaultCurrency}</Td>
                    <Td align="end" className="tabular text-muted">{client._count.projects}</Td>
                    <Td align="end" className="tabular text-muted">{client._count.invoices}</Td>
                    <Td><Badge tone={statusTone(client.status)}>{enumLabel(client.status, locale)}</Badge></Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

    </>
  );
}

export function ClientDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState('overview');
  const navigate = useNavigate();

  const { data: client, isLoading, error, refetch } = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get<any>(`/clients/${id}`),
  });

  const setStatus = useMutation({
    mutationFn: (active: boolean) => api.post(`/clients/${id}/${active ? 'reactivate' : 'deactivate'}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', id] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const isActive = client.status === 'ACTIVE';

  return (
    <>
      <div className="mb-4">
        <Link to="/clients" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('clients.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {client.logo && (
              // On white, as on the contracts it is printed on.
              <span className="grid h-12 w-28 shrink-0 place-items-center rounded-md border hairline bg-white p-1.5">
                <img
                  src={client.logo.url}
                  alt={t('clients.logoAlt', { client: client.companyName })}
                  className="max-h-full max-w-full object-contain"
                />
              </span>
            )}
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{client.code}</span>
            <span>{locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(client.status)}>{enumLabel(client.status, locale)}</Badge>
            <Badge tone="neutral">{localized(client.country)}</Badge>
            <Badge tone="info">{client.defaultCurrency}</Badge>
          </span>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            {can(P.CLIENTS_UPDATE) && (
              <Button onClick={() => navigate(`/clients/${id}/edit`)}>{t('common.edit')}</Button>
            )}
            {can(P.CLIENTS_DEACTIVATE) && (
              <Button
                variant={isActive ? 'danger' : 'primary'}
                loading={setStatus.isPending}
                onClick={() => setStatus.mutate(!isActive)}
              >
                {isActive ? t('clients.deactivate') : t('clients.reactivate')}
              </Button>
            )}
          </div>
        }
      />

      {!isActive && (
        <Card className="mb-4 border-warning/40">
          <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('clients.inactiveWarning')}</p>
        </Card>
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: t('clients.overview') },
          { id: 'contacts', label: t('clients.contacts'), count: client.contacts.length },
          { id: 'projects', label: t('nav.projects'), count: client._count.projects },
          { id: 'tickets', label: t('nav.tickets'), count: client._count.tickets },
          ...(can(P.CLIENT_CRS_MANAGE)
            ? [{ id: 'change-requests', label: t('clients.changeRequests'), count: client._count.changeRequests }]
            : []),
          // The Drive folders and files about it that the reader may open (2026-11-02).
          { id: 'drive', label: t('drive.tab') },
          // Clients are managed in both systems; their invoices, payments,
          // contracts and financial summary are Finance's, and the API refuses
          // them here.
        ]}
      />

      <div className="mt-5">
        {tab === 'overview' && <OverviewTab client={client} />}
        {tab === 'contacts' && <ContactsTab client={client} />}
        {tab === 'projects' && <ProjectsTab clientId={id} />}
        {tab === 'tickets' && <TicketsTab clientId={id} />}
        {tab === 'change-requests' && <ChangeRequestsTab clientId={id} />}
        {tab === 'drive' && <DriveRelated clientId={id} />}
      </div>

    </>
  );
}

function OverviewTab({ client }: { client: any }) {
  const { t, localized } = useI18n();
  return (
    // grid-cols-1, not the implicit column: that one grows to the widest card, and the
    // sign-in card's title and button pushed a phone's page sideways (2026-10-11).
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader title={t('clients.company')} />
        <dl className="divide-y divide-[var(--line)]">
          <Detail label={t('clients.companyName')}>{client.companyName}</Detail>
          {client.companyNameAr && <Detail label={t('clients.companyNameAr')}>{client.companyNameAr}</Detail>}
          {client.legalName && <Detail label={t('clients.legalName')}>{client.legalName}</Detail>}
          <Detail label={t('clients.country')}>{localized(client.country)}</Detail>
          <Detail label={t('clients.currency')}>
            <span className="font-mono">{client.defaultCurrency}</span>
            <span className="ms-2 text-muted">{localized(client.currency)}</span>
          </Detail>
          {client.taxNumber && <Detail label={t('clients.taxNumber')}>{client.taxNumber}</Detail>}
          {client.registrationNumber && (
            <Detail label={t('clients.registrationNumber')}>{client.registrationNumber}</Detail>
          )}
          {client.website && (
            <Detail label={t('clients.website')}>
              <a href={client.website} target="_blank" rel="noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                {client.website}
              </a>
            </Detail>
          )}
        </dl>
      </Card>

      <Card>
        <CardHeader title={t('clients.address')} />
        <p className="whitespace-pre-wrap text-body-sm">{client.address || '—'}</p>
        {client.notes && (
          <div className="mt-4 border-t hairline pt-3">
            <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('common.notes')}</p>
            <p className="whitespace-pre-wrap text-body-sm">{client.notes}</p>
          </div>
        )}
      </Card>

      <TicketDepartmentsCard client={client} />
      <CallersCard client={client} />
      <PortalCard client={client} />
    </div>
  );
}

/**
 * The employees the client may call from the portal — chosen on its form by an
 * administrator (operator, 2026-11-01). Nobody chosen: the client calls nobody.
 */
function CallersCard({ client }: { client: any }) {
  const { t, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const callers: Array<{ id: string; fullName: string; fullNameAr: string | null; isActive: boolean }> = client.callableUsers ?? [];

  return (
    <Card>
      <CardHeader
        title={t('clients.callers')}
        subtitle={t('clients.callersWhere')}
        action={
          can(P.CLIENT_PORTAL_MANAGE) &&
          can(P.CLIENTS_UPDATE) && (
            <Button size="sm" onClick={() => navigate(`/clients/${client.id}/edit`)}>
              {t('common.edit')}
            </Button>
          )
        }
      />
      {callers.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {callers.map((person) => (
            <li key={person.id}>
              <Badge tone={person.isActive ? 'info' : 'neutral'}>
                {localized(person)}
                {!person.isActive && ` · ${t('clients.callerInactive')}`}
              </Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-body-sm text-muted">{t('clients.callersNone')}</p>
      )}
    </Card>
  );
}

/**
 * The departments the client may send its portal tickets to — chosen on its
 * form by an administrator (operator, 2026-10-11).
 */
function TicketDepartmentsCard({ client }: { client: any }) {
  const { t, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const departments: Array<{ id: string; name: string; nameAr: string | null; isActive: boolean }> =
    client.ticketDepartments ?? [];
  const usable = departments.filter((d) => d.isActive);

  return (
    <Card>
      <CardHeader
        title={t('clients.ticketDepartments')}
        subtitle={t('clients.ticketDepartmentsWhere')}
        action={
          can(P.CLIENT_PORTAL_MANAGE) &&
          can(P.CLIENTS_UPDATE) && (
            <Button size="sm" onClick={() => navigate(`/clients/${client.id}/edit`)}>
              {t('common.edit')}
            </Button>
          )
        }
      />
      {departments.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {departments.map((department) => (
            <li key={department.id}>
              <Badge tone={department.isActive ? 'info' : 'neutral'}>
                {localized(department)}
                {!department.isActive && ` · ${t('clients.ticketDepartmentOff')}`}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {usable.length === 0 && (
        <p className={'text-body-sm text-warning-fg dark:text-warning-dark' + (departments.length ? ' mt-3' : '')}>
          {t('clients.ticketDepartmentsShut')}
        </p>
      )}
    </Card>
  );
}

/** The company's own sign-in to the client portal, for its owners — set on the client's form. */
function PortalCard({ client }: { client: any }) {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const account = client.account as
    | { username: string; isActive: boolean; lastLoginAt: string | null; passwordChangedAt: string }
    | null;
  // Contacts with a sign-in of their own that works.
  const contactSignIns = (client.contacts ?? []).filter((c: any) => c.account?.isActive).length;

  return (
    <Card>
      <CardHeader
        title={t('clients.portal')}
        subtitle={t('clients.portalWhere')}
        action={
          can(P.CLIENT_PORTAL_MANAGE) &&
          can(P.CLIENTS_UPDATE) && (
            <Button size="sm" onClick={() => navigate(`/clients/${client.id}/edit`)}>
              {account ? t('common.edit') : t('clients.portalGive')}
            </Button>
          )
        }
      />
      {account ? (
        <dl className="divide-y divide-[var(--line)]">
          <Detail label={t('common.status')}>
            <Badge tone={account.isActive ? 'success' : 'neutral'}>
              {account.isActive ? t('clients.portalOn') : t('clients.portalOff')}
            </Badge>
          </Detail>
          <Detail label={t('clients.portalUsername')}>
            <bdi dir="ltr" className="font-mono text-mono-sm">{account.username}</bdi>
          </Detail>
          <Detail label={t('clients.portalLastSignIn')}>
            {account.lastLoginAt ? formatDateTime(account.lastLoginAt, locale) : t('clients.portalNever')}
          </Detail>
        </dl>
      ) : (
        <p className="text-body-sm text-muted">{t('clients.portalNone')}</p>
      )}
      <p className="mt-3 border-t hairline pt-3 text-caption text-muted">
        {contactSignIns > 0 ? t('clients.contactSignIns', { count: contactSignIns }) : t('clients.contactSignInsNone')}
      </p>
    </Card>
  );
}

function TicketsTab({ clientId }: { clientId: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['tickets', { clientId }],
    queryFn: () => api.get<any>('/tickets', { clientId, pageSize: 50 }),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data?.items?.length) return <Card><EmptyState /></Card>;

  return (
    <Card padded={false}>
      <Table>
        <thead>
          <tr>
            <Th>{t('tickets.number')}</Th>
            <Th>{t('tickets.subject')}</Th>
            <Th>{t('common.status')}</Th>
            <Th align="end">{t('common.createdAt')}</Th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((ticket: any) => (
            <Tr key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)}>
              <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{ticket.number}</span></Td>
              <Td className="max-w-md">
                <span className="block truncate">{ticket.subject}</span>
                {ticket.contact && (
                  <span className="block truncate text-caption text-faint">
                    {t('tickets.requestedByName', { name: ticket.contact.name })}
                  </span>
                )}
                {ticket.requesterAccountId && (
                  <Badge tone="plasma" className="mt-1">{t('tickets.raisedOnPortal')}</Badge>
                )}
              </Td>
              <Td><Badge tone={statusTone(ticket.status)}>{enumLabel(ticket.status, locale)}</Badge></Td>
              <Td align="end" className="whitespace-nowrap text-caption text-muted">
                {formatDateTime(ticket.createdAt, locale)}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function ChangeRequestsTab({ clientId }: { clientId: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['client-crs', { clientId }],
    queryFn: () => api.get<any>('/client-change-requests', { clientId, pageSize: 50 }),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data?.items?.length) return <Card><EmptyState /></Card>;

  return (
    <Card padded={false}>
      <Table>
        <thead>
          <tr>
            <Th>{t('crs.number')}</Th>
            <Th>{t('crs.titleField')}</Th>
            <Th>{t('common.status')}</Th>
            <Th align="end">{t('common.createdAt')}</Th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((request: any) => (
            <Tr key={request.id} onClick={() => navigate(`/client-crs/${request.id}`)}>
              <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{request.number}</span></Td>
              <Td className="max-w-md"><span className="block truncate">{request.title}</span></Td>
              <Td><Badge tone={statusTone(request.status)}>{crStatusLabel(t, request.status)}</Badge></Td>
              <Td align="end" className="whitespace-nowrap text-caption text-muted">
                {formatDateTime(request.createdAt, locale)}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function ContactsTab({ client }: { client: any }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const remove = useMutation({
    mutationFn: (contactId: string) => api.del(`/clients/${client.id}/contacts/${contactId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['client', client.id] }),
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Card padded={false}>
      <div className="flex items-center justify-between gap-3 p-4">
        <p className="text-body-sm text-muted">{t('clients.contacts')}</p>
        {can(P.CLIENT_CONTACTS_MANAGE) && (
          <Button
            icon={<Plus className="h-4 w-4" />}
            onClick={() => navigate(`/clients/${client.id}/contacts/new`)}
          >
            {t('clients.newContact')}
          </Button>
        )}
      </div>
      {!client.contacts.length ? (
        <EmptyState />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('clients.jobTitle')}</Th>
              <Th>{t('common.email')}</Th>
              <Th>{t('common.phone')}</Th>
              <Th>{t('clients.contactPortalColumn')}</Th>
              <Th align="end">{t('common.actions')}</Th>
            </tr>
          </thead>
          <tbody>
            {client.contacts.map((contact: any) => (
              <Tr key={contact.id}>
                <Td>
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    {contact.isPrimary && <Star className="h-3.5 w-3.5 text-warning-fg dark:text-warning-dark" aria-label={t('clients.primary')} />}
                    <bdi dir="ltr">{contact.name}</bdi>
                    {/* Contacts are kept in English (2026-10-12); one saved before in Arabic says so until edited. */}
                    {[contact.name, contact.jobTitle, contact.email, contact.phone].some((value) => !isEnglish(value)) && (
                      <Badge tone="warning">{t('clients.contactNotEnglish')}</Badge>
                    )}
                  </span>
                </Td>
                <Td className="text-muted">{contact.jobTitle ? <bdi dir="ltr">{contact.jobTitle}</bdi> : '—'}</Td>
                <Td className="text-muted">{contact.email ? <bdi dir="ltr">{contact.email}</bdi> : '—'}</Td>
                <Td className="text-muted">{contact.phone ? <bdi dir="ltr">{contact.phone}</bdi> : '—'}</Td>
                <Td>
                  {/* Their own sign-in to the client portal, if they were given one (2026-10-11). */}
                  {contact.account ? (
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <bdi dir="ltr" className="font-mono text-mono-sm">{contact.account.username}</bdi>
                      <Badge tone={contact.account.isActive ? 'success' : 'neutral'}>
                        {contact.account.isActive ? t('clients.portalOn') : t('clients.portalOff')}
                      </Badge>
                    </span>
                  ) : (
                    <span className="text-muted">{t('clients.contactNoSignIn')}</span>
                  )}
                </Td>
                <Td align="end">
                  {(can(P.CLIENT_CONTACTS_MANAGE) || can(P.CLIENT_CONTACTS_REMOVE)) && (
                    <span className="flex justify-end gap-2">
                      {can(P.CLIENT_CONTACTS_MANAGE) && (
                        <Button onClick={() => navigate(`/clients/${client.id}/contacts/${contact.id}/edit`)}>
                          {t('common.edit')}
                        </Button>
                      )}
                      {/* Team leaders add and edit contacts, and remove none (operator, 2026-10-11). */}
                      {can(P.CLIENT_CONTACTS_REMOVE) && (
                        <Button
                          variant="danger"
                          icon={<Trash2 className="h-4 w-4" />}
                          aria-label={t('common.remove')}
                          title={t('common.remove')}
                          onClick={() => remove.mutate(contact.id)}
                        />
                      )}
                    </span>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function ProjectsTab({ clientId }: { clientId: string }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['client-projects', clientId],
    queryFn: () => api.get<any[]>(`/clients/${clientId}/projects`),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data?.length) return <Card><EmptyState /></Card>;

  return (
    <Card padded={false}>
      <Table>
        <thead>
          <tr>
            <Th>{t('common.code')}</Th>
            <Th>{t('common.name')}</Th>
            <Th>{t('common.status')}</Th>
            <Th align="end">{t('nav.tickets')}</Th>
            <Th align="end">{t('nav.tasks')}</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((project) => (
            <Tr key={project.id} onClick={() => navigate(`/projects/${project.id}`)}>
              <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{project.code}</span></Td>
              <Td>{localized(project)}</Td>
              <Td><Badge tone={statusTone(project.status)}>{projectStatusLabel(project.status, locale)}</Badge></Td>
              <Td align="end" className="tabular text-muted">{project._count.tickets}</Td>
              <Td align="end" className="tabular text-muted">{project._count.tasks}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
